import { Injectable } from '@angular/core';
import { GenderTypes } from 'src/app/feed-advice/models/feed-advice.model';
import { Discount } from 'src/app/settings/models/settings.model';
import { FEED_PROFIT_MANURE_VALUES } from 'src/assets/data/feedProfitConstants';

import { FeedProfitCalculations } from './feed-profit-calculations.service';
import {
  Calculation,
  CountryOptions,
  ExtendedSimulation,
  ExtendedSimulationExtendedBaseLine,
  ExtendedSimulationFullObject,
  FeederType,
  GenderOptions,
} from '../models/feed-profit.model';

@Injectable({
  providedIn: 'root',
})
export class FeedProfitExtendedSimulationsCalculationService {
  constructor(private readonly calculationsService: FeedProfitCalculations) {}

  // Calculates J17
  getOccupancyRate(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    livingWeight?: number,
    totalgrowth?: number,
  ) {
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    totalgrowth =
      totalgrowth ||
      this.getTotalGrowth(
        calculation,
        simulation,
        isComparison,
        livingWeight,
        this.getAmountOfDays(calculation, simulation),
      );
    const slaughteredWeight = livingWeight - simulation.startWeight;
    const growthPerSlaughteredKg = slaughteredWeight / (totalgrowth * 0.001);

    const day2GrowthPerSlaughteredKgCalculation =
      growthPerSlaughteredKg +
      this.calculationsService.toExcelPercentage(
        calculation.extendedBaseLine.deliveries[0].percentage,
      ) *
        calculation.extendedBaseLine.deliveries[1].days;

    const returnValue =
      calculation.extendedBaseLine.deliveries[2].percentage > 0
        ? this.calculationsService.toVisualPercentage(
            growthPerSlaughteredKg /
              (day2GrowthPerSlaughteredKgCalculation +
                (this.calculationsService.toExcelPercentage(
                  calculation.extendedBaseLine.deliveries[0].percentage,
                ) +
                  this.calculationsService.toExcelPercentage(
                    calculation.extendedBaseLine.deliveries[1].percentage,
                  )) *
                  calculation.extendedBaseLine.deliveries[2].days +
                calculation.extendedBaseLine.cleaning),
          )
        : this.calculationsService.toVisualPercentage(
            growthPerSlaughteredKg /
              (day2GrowthPerSlaughteredKgCalculation + calculation.extendedBaseLine.cleaning),
          );
    return returnValue;
  }

  // Calculates S50
  getPigFeedSum(
    _calculation: Calculation,
    simulation: ExtendedSimulation,
    _isComparison = false,
    _livingWeight?: number,
  ) {
    let PigFeedSum = 0;
    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    _livingWeight =
      _livingWeight || this.calculationsService.getLivingWeight(_calculation, simulation);

    const allFeedersFeed = allFeeders.map((feeder) => {
      //get averages per feeder
      const feederFeed =
        feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;
      return feederFeed;
    });

    allFeedersFeed.forEach((feederAvg) => (PigFeedSum += feederAvg));
    return PigFeedSum;
  }

  // Calculates N51
  getFeedPackagePrice(simulation: ExtendedSimulation) {
    let totalKg = 0;
    let totalCosts = 0;

    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    const calculatedFeederValues = allFeeders.map((feeder) => {
      const feederFeed =
        feeder?.genderFeedRatios
          ?.map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder?.genderFeedRatios?.length;

      return {
        kg: feederFeed,
        costs: feederFeed * feeder.product.grossPricePer100Kg * 0.01,
        type: feeder.type,
      };
    });
    calculatedFeederValues.forEach((feeder) => {
      totalCosts += feeder.costs;
      totalKg += feeder.kg;
    });
    const packagePrice = totalCosts / (totalKg * 0.01);
    return packagePrice;
  }

  // Calculates J15
  getAmountOfDays(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison = false,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.calculationsService.getCalculationLivingWeight(calculation);

    const calculationGrowthPerSlaughteredWeight =
      (calculationLivingWeight - calculation.extendedBaseLine.startWeight) /
      (calculation.extendedBaseLine.growth * 0.001);
    if (isComparison) {
      return calculationGrowthPerSlaughteredWeight;
    }
    const divergingDaysStartWeight = this.getDivergingDaysStartWeight(calculation, simulation);
    const divergingDaysSlaughteredWeight = this.getDivergingDaysSlaughteredWeight(
      calculation,
      simulation,
      livingWeight,
      calculationLivingWeight,
    );
    const divergingDaysGrowth = this.getDivergingDaysGrowth(calculation, calculationLivingWeight);

    const amountOfDays =
      calculationGrowthPerSlaughteredWeight +
      divergingDaysStartWeight +
      divergingDaysSlaughteredWeight +
      divergingDaysGrowth;
    return amountOfDays;
  }

  // Calculates J16
  getTotalGrowth(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    livingWeight: number,
    amountOfDays: number,
  ) {
    if (isComparison) {
      return simulation.extendedBaseLine.growth;
    }
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    amountOfDays =
      amountOfDays ?? this.getAmountOfDays(calculation, simulation, null, livingWeight);

    const totalGrowth = ((livingWeight - simulation.startWeight) / amountOfDays) * 1000;
    return totalGrowth;
  }

  // Calculates J12
  getDivergingDaysStartWeight(calculation: Calculation, simulation: ExtendedSimulation) {
    return simulation.startWeight < calculation.extendedComparisonSimulation.startWeight
      ? ((simulation.startWeight - calculation.extendedComparisonSimulation.startWeight) /
          (calculation.baseLine.growthStartProcedure * 0.001)) *
          -1
      : (calculation.extendedComparisonSimulation.startWeight - simulation.startWeight) /
          (calculation.baseLine.growthStartProcedure * 0.001);
  }

  getDivergingDaysSlaughteredWeight(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight =
      livingWeight || this.calculationsService.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight || this.calculationsService.getCalculationLivingWeight(calculation);

    const weight = Math.abs(livingWeight - calculationLivingWeight);

    return Math.abs(weight / (calculation.baseLine.growthEndProcedure * 0.001));
  }

  getFeedPrice(
    simulation: ExtendedSimulation,
    PigFeedSum: number,
    livingWeight: number,
    feedPacketPrice?: number,
  ) {
    feedPacketPrice = feedPacketPrice || this.getFeedPackagePrice(simulation);

    return (feedPacketPrice * PigFeedSum * 0.01) / (livingWeight - simulation.startWeight);
  }

  // Calculates J30
  getFeedCostTotalCostTotalKgForPacketPriceForFeedCosts(simulation: ExtendedSimulation) {
    let totalKg = 0;
    let totalCosts = 0;

    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    allFeeders
      .map((feeder) => {
        const feederFeed =
          feeder.genderFeedRatios
            .map((ratio) => ratio.feed)
            .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;
        return {
          kg: feederFeed,
          costs: feeder.product.grossPricePer100Kg,
        };
      })
      .forEach((feeder) => {
        totalCosts += feeder.costs * feeder.kg * 0.01;
        totalKg += feeder.kg;
      }); //totalcosts = j50
    const feedcosts = (totalCosts / totalKg) * 100;
    return { feedcosts, totalCosts, totalKg };
  }

  // Calculates J31
  getEffectOnFeedCostsStartWeightHas(calculation: Calculation, simulation: ExtendedSimulation) {
    const startFeederPrice = this.calculationsService.getSpecificFeederFeedPrice(
      calculation,
      FeederType.START_FEEDER,
    ); //H51
    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    const startFeeders = allFeeders.filter((feeder) => feeder.type === FeederType.START_FEEDER);
    const avgEnergyStartFeeders =
      startFeeders
        .map((feeder) => feeder.product.ewKg)
        .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / startFeeders.length;
    const divergingEnergyStartWeight =
      this.calculationsService.getDivergingEnergyFromGrowthStartStartWeight(
        calculation,
        simulation,
        //calculation.extendedComparisonSimulation,
      );

    return ((startFeederPrice / 100) * divergingEnergyStartWeight) / avgEnergyStartFeeders;
  }

  getMaxEnergySimulation(_calculation: Calculation, simulation: ExtendedSimulation) {
    let simMaxEnergy = 0;

    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    const mappedFeeders = allFeeders.map((feeder) => {
      const feederFeed =
        feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

      return {
        actual: feederFeed * feeder.product.ewKg,
        calc: `(${feederFeed} * ${feeder.product.ewKg})`,
      };
    });
    mappedFeeders.forEach((feeder) => (simMaxEnergy += feeder.actual));

    return simMaxEnergy;
  }

  // Calculates M50
  getAverageEnergy(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    maxEnergySimulation?: number,
    pigFeedSum?: number,
  ) {
    maxEnergySimulation =
      maxEnergySimulation ?? this.getMaxEnergySimulation(calculation, simulation);
    pigFeedSum = pigFeedSum ?? this.getPigFeedSum(calculation, simulation);

    const averageEnergy = maxEnergySimulation / pigFeedSum;

    return averageEnergy;
  }

  getAverageEnergyConversion(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    pigFeedSum?: number,
    averageEnergy?: number,
    livingWeight?: number,
  ) {
    pigFeedSum = pigFeedSum ?? this.getPigFeedSum(calculation, simulation);
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    averageEnergy =
      averageEnergy ?? this.getAverageEnergy(calculation, simulation, undefined, pigFeedSum);
    const averageEnergyConversion =
      (pigFeedSum * averageEnergy) / (livingWeight - simulation.startWeight);

    return averageEnergyConversion;
  }

  getEnergyUsed(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    AverageEnergyConversion?: number,
    livingWeight?: number,
  ) {
    AverageEnergyConversion =
      AverageEnergyConversion ?? this.getAverageEnergyConversion(calculation, simulation);
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    const energyUsed = AverageEnergyConversion * (livingWeight - simulation.startWeight);

    return energyUsed;
  }

  // Calculates J36
  getFeedCosts(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    EnergyExpended?: number,
    AverageEnergy?: number,
    feedPacketPrice?: number,
  ) {
    feedPacketPrice =
      feedPacketPrice ||
      this.getFeedCostTotalCostTotalKgForPacketPriceForFeedCosts(simulation).feedcosts;
    EnergyExpended = EnergyExpended ?? this.getEnergyUsed(calculation, simulation);
    AverageEnergy = AverageEnergy ?? this.getAverageEnergy(calculation, simulation);
    const feedCost = (EnergyExpended / AverageEnergy) * (feedPacketPrice * 0.01);

    return feedCost;
  }

  getAverageDeliveredGrowFinisherBaseSimulation(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    occupancyRate?: number,
  ) {
    occupancyRate = occupancyRate ?? this.getOccupancyRate(calculation, simulation, true);

    return calculation.companyDetails.amountOfGrowFinisher * (occupancyRate * 0.01);
  }

  getAverageDeliveredGrowFinisher(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    occupancyRate?: number,
  ) {
    occupancyRate = occupancyRate ?? this.getOccupancyRate(calculation, simulation, isComparison);

    const averageDeliveredGrowFinisher = isComparison
      ? calculation.companyDetails?.amountOfGrowFinisher *
        this.calculationsService.toExcelPercentage(occupancyRate)
      : ((calculation.companyDetails?.amountOfGrowFinisher *
          calculation.extendedBaseLine?.squareMeterPerGrowFinisher) /
          simulation.extendedBaseLine.squareMeterPerGrowFinisher) *
        this.calculationsService.toExcelPercentage(occupancyRate);
    return averageDeliveredGrowFinisher;
  }

  getAverageDeliveredGrowFinisherAlternativeSimulation(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    occupancyRate?: number,
  ) {
    occupancyRate = occupancyRate ?? this.getOccupancyRate(calculation, simulation, false);

    return (
      (calculation.companyDetails.amountOfGrowFinisher *
        calculation.extendedBaseLine.squareMeterPerGrowFinisher) /
      (occupancyRate * 0.01)
    );
  }

  // Calculates J37, F37
  getCostDeathsAndSpoilage(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    avgAmountDeliveredGrowFinisher?: number,
    averageEnergyConversion?: number,
    averageEnergy?: number,
    feedCosts?: number,
    profitGrowFinisher?: number,
    feedPacketPrice?: number,
  ) {
    const livingWeight = this.calculationsService.getLivingWeight(calculation, simulation);

    if (!avgAmountDeliveredGrowFinisher) {
      const amountofDays = this.getAmountOfDays(
        calculation,
        simulation,
        isComparison,
        livingWeight,
        this.calculationsService.getCalculationLivingWeight(calculation),
      );
      const totalGrowth = this.getTotalGrowth(
        calculation,
        simulation,
        isComparison,
        livingWeight,
        amountofDays,
      );
      avgAmountDeliveredGrowFinisher = this.getAverageDeliveredGrowFinisher(
        calculation,
        simulation,
        isComparison,
        this.getOccupancyRate(calculation, simulation, isComparison, livingWeight, totalGrowth),
      );
    }
    averageEnergyConversion =
      averageEnergyConversion ?? this.getAverageEnergyConversion(calculation, simulation);
    averageEnergy = averageEnergy ?? this.getAverageEnergy(calculation, simulation);
    feedCosts =
      feedCosts ||
      this.getFeedCosts(calculation, simulation, undefined, averageEnergy, feedPacketPrice);
    profitGrowFinisher =
      profitGrowFinisher ?? this.calculationsService.getProfitGrowFinisher(simulation);
    feedPacketPrice = feedPacketPrice || this.getFeedPackagePrice(simulation);

    const calculatedPigletPrice = this.calculationsService.getPigletPrice(calculation, simulation);
    const calcMortalityPercentage = this.calculationsService.toExcelPercentage(
      calculation.extendedBaseLine.mortalityPercentage,
    );

    const costDeathsAndSpoilage =
      (avgAmountDeliveredGrowFinisher * calcMortalityPercentage * calculatedPigletPrice +
        (((avgAmountDeliveredGrowFinisher *
          calcMortalityPercentage *
          (calculation.baseLine.averageWeightPerDeadGrowFinisher - simulation.startWeight) *
          averageEnergyConversion) /
          averageEnergy) *
          feedPacketPrice) /
          100) /
        avgAmountDeliveredGrowFinisher +
      (avgAmountDeliveredGrowFinisher *
        calcMortalityPercentage *
        (profitGrowFinisher - calculatedPigletPrice - feedCosts)) /
        avgAmountDeliveredGrowFinisher;
    return costDeathsAndSpoilage;
  }

  getFeedProfitAverageDeliveredGrowFinisher(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    profitGrowFinisher?: number,
    pigletPrice?: number,
    feedCosts?: number,
    costsDeathsAndSpoilage?: number,
  ) {
    profitGrowFinisher =
      profitGrowFinisher ?? this.calculationsService.getProfitGrowFinisher(simulation);
    pigletPrice = pigletPrice ?? this.calculationsService.getPigletPrice(calculation, simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    costsDeathsAndSpoilage =
      costsDeathsAndSpoilage ||
      this.getCostDeathsAndSpoilage(
        calculation,
        simulation,
        undefined,
        undefined,
        undefined,
        undefined,
        feedCosts,
        profitGrowFinisher,
        undefined,
      );

    return profitGrowFinisher - pigletPrice - feedCosts - costsDeathsAndSpoilage;
  }

  // Calculates J21
  getRounds(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    amountOfDays?: number,
    occupancyRate?: number,
  ) {
    amountOfDays = amountOfDays ?? this.getAmountOfDays(calculation, simulation, isComparison);
    occupancyRate = occupancyRate ?? this.getOccupancyRate(calculation, simulation, isComparison);

    const rounds = (365 / amountOfDays) * (occupancyRate * 0.01);

    return rounds;
  }

  getManureCostPerPig(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    totalGrowth?: number,
    feedConversion?: number,
    PigFeedSum?: number,
    livingWeight?: number,
    amountOfDays?: number,
  ) {
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    totalGrowth =
      totalGrowth ??
      this.getTotalGrowth(calculation, simulation, isComparison, livingWeight, amountOfDays);

    PigFeedSum = PigFeedSum ?? this.getPigFeedSum(calculation, simulation);

    feedConversion =
      feedConversion ??
      this.calculationsService.getFeedConversion(simulation, PigFeedSum, livingWeight);

    const feedInKG = (totalGrowth / 1000) * feedConversion * 365;
    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    const feedProductContributions = allFeeders.map((feeder) => {
      const feederFeed =
        feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

      return {
        product: feeder.product,
        contributionPercentage: feederFeed / PigFeedSum,
      };
    });

    let reSum = 0;
    feedProductContributions.forEach(
      (feeder) => (reSum += feeder.product.proteinGramPerKg * feeder.contributionPercentage),
    );
    const reFeed = feedInKG * reSum;
    const meatProduct = (totalGrowth / 1000) * 365;
    const reMeat = meatProduct * 25 * 6.25;
    const reRemainder = reFeed - reMeat;

    const urineProduction =
      0.0045 * reRemainder +
      0.035 * FEED_PROFIT_MANURE_VALUES.K * feedInKG +
      FEED_PROFIT_MANURE_VALUES.Na * feedInKG * 0.055;
    const fecesProduction = feedInKG * 0.16 * 0.88 * 2.2;
    const manureCubedProduction = (urineProduction + fecesProduction) / 1000;
    const priceForCubeOfManure = calculation.extendedBaseLine.fertilizerPricePerCubicMeter;

    return manureCubedProduction * priceForCubeOfManure;
  }

  getFeedProfitAverageGrowFinisherYear(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    amountOfRounds?: number,
    profitGrowFinisher?: number,
    pigletPrice?: number,
    feedCosts?: number,
    costsDeathsAndSpoilage?: number,
  ) {
    amountOfRounds =
      amountOfRounds ||
      this.getRounds(
        calculation,
        simulation,
        isComparison,
        this.getAmountOfDays(calculation, simulation, isComparison),
        this.getOccupancyRate(calculation, simulation, isComparison),
      );
    profitGrowFinisher =
      profitGrowFinisher ?? this.calculationsService.getProfitGrowFinisher(simulation);
    pigletPrice = pigletPrice ?? this.calculationsService.getPigletPrice(calculation, simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);

    costsDeathsAndSpoilage =
      costsDeathsAndSpoilage ||
      this.getCostDeathsAndSpoilage(
        calculation,
        simulation,
        isComparison,
        undefined,
        undefined,
        undefined,
        feedCosts,
        profitGrowFinisher,
        undefined,
      );

    const feedProfitAverageGrowFinisherInYear = amountOfRounds * profitGrowFinisher;

    const pigletPriceInYear = amountOfRounds * pigletPrice;
    const feedCostsInYear = amountOfRounds * feedCosts;
    const costsDeathsAndSpoilageInYear = amountOfRounds * costsDeathsAndSpoilage;

    const feedProfitAverageGrowFinisherYear =
      feedProfitAverageGrowFinisherInYear -
      pigletPriceInYear -
      feedCostsInYear -
      costsDeathsAndSpoilageInYear;

    return feedProfitAverageGrowFinisherYear;
  }

  getFeedProfitPerSquareMeterIncludingFertilizer(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    feedprofitAverageGrowFinisherYear?: number,
    manureCostPerPig?: number,
  ) {
    feedprofitAverageGrowFinisherYear =
      feedprofitAverageGrowFinisherYear ||
      this.getFeedProfitAverageGrowFinisherYear(calculation, simulation, isComparison);
    manureCostPerPig =
      manureCostPerPig ?? this.getManureCostPerPig(calculation, simulation, isComparison);

    const feedProfitPerSquareMeterInclFertilizer =
      (feedprofitAverageGrowFinisherYear - manureCostPerPig) /
      calculation.extendedBaseLine.squareMeterPerGrowFinisher;

    return feedProfitPerSquareMeterInclFertilizer;
  }

  getFeedProfitAveragePresentGrowFinisher(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    amountOfRounds?: number,
    profitPerAverageDeliveredGrowFinisher?: number,
    pigletPrice?: number,
    feedCosts?: number,
    deathsCost?: number,
  ) {
    amountOfRounds = amountOfRounds ?? this.getRounds(calculation, simulation, isComparison);
    profitPerAverageDeliveredGrowFinisher =
      profitPerAverageDeliveredGrowFinisher ??
      this.calculationsService.getProfitGrowFinisher(simulation);
    pigletPrice = pigletPrice ?? this.calculationsService.getPigletPrice(calculation, simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    deathsCost = deathsCost ?? this.getCostDeathsAndSpoilage(calculation, simulation, isComparison);

    const feedProfitAveragePresentGrowFinisher =
      (profitPerAverageDeliveredGrowFinisher - pigletPrice - feedCosts - deathsCost) *
      amountOfRounds;

    return feedProfitAveragePresentGrowFinisher;
  }

  // Calculates J38, F38
  getFinalDifferenceDelivered(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    profitGrowFinisher?: number,
    feedCosts?: number,
    costsDeathsAndSpoilage?: number,
    pigletPrice?: number,
  ) {
    profitGrowFinisher =
      profitGrowFinisher ?? this.calculationsService.getProfitGrowFinisher(simulation);
    feedCosts = feedCosts ?? this.getFeedCosts(calculation, simulation);
    costsDeathsAndSpoilage =
      costsDeathsAndSpoilage ??
      this.getCostDeathsAndSpoilage(calculation, simulation, isComparison);
    pigletPrice = pigletPrice ?? this.calculationsService.getPigletPrice(calculation, simulation);

    const finalDifference = profitGrowFinisher - pigletPrice - feedCosts - costsDeathsAndSpoilage;

    return finalDifference;
  }

  getAverageAmountOfPresentDeliveredGrowFinishers(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    occupancyRate?: number,
  ) {
    occupancyRate = occupancyRate || this.getOccupancyRate(calculation, simulation, false);

    const averageAmountOfPresentDeliveredGrowFinishers =
      ((calculation.companyDetails.numberOfSilos +
        calculation.extendedBaseLine.squareMeterPerGrowFinisher) /
        simulation.extendedBaseLine.squareMeterPerGrowFinisher) *
      occupancyRate;
    return averageAmountOfPresentDeliveredGrowFinishers;
  }

  getMeatPercentage(calculation: Calculation, simulation: ExtendedSimulation) {
    return (
      0.6686 -
      0.006549 * this.getMilimeterLard(calculation, simulation) +
      0.000207 * this.getMilimeterMuscle(calculation, simulation)
    );
  }

  getMilimeterLard(calculation: Calculation, simulation: ExtendedSimulation) {
    return (
      calculation.extendedBaseLine.backFatThicknessInMillimeters +
      (simulation.extendedBaseLine.slaughteredWeightInKg -
        calculation.extendedComparisonSimulation.extendedBaseLine.slaughteredWeightInKg) *
        0.15
    );
  }

  getMilimeterMuscle(calculation: Calculation, simulation: ExtendedSimulation) {
    return (
      calculation.extendedBaseLine.loinDepthThicknessInMillimeters +
      (simulation.extendedBaseLine.slaughteredWeightInKg -
        calculation.extendedComparisonSimulation.extendedBaseLine.slaughteredWeightInKg) *
        0.29
    );
  }

  getEffectOnFeedCostsSlaughteredWeightHas(
    calculation: Calculation,
    simulation: ExtendedSimulation,
  ) {
    const endFeederPrice = this.calculationsService.getSpecificFeederFeedPrice(
      calculation,
      FeederType.END_FEEDER,
    ); //H53
    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    const endFeeders = allFeeders.filter((feeder) => feeder.type === FeederType.END_FEEDER);
    const avgEnergyEndFeeders =
      endFeeders
        .map((feeder) => feeder.product.ewKg)
        .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / endFeeders.length;
    const divergingEnergyEndWeight =
      this.calculationsService.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
      );

    return ((endFeederPrice / 100) * divergingEnergyEndWeight) / avgEnergyEndFeeders;
  }

  getEffectOnFeedCostsGrowthHas(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    packetPrice?: number,
  ) {
    packetPrice = packetPrice || this.getFeedPackagePrice(simulation);

    const divergingEnergyEndWeight = this.getDivergingDaysGrowth(calculation);

    return (packetPrice / 100) * divergingEnergyEndWeight;
  }

  getFeedCostPerKgPerGrowth(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.calculationsService.getCalculationLivingWeight(calculation);

    let totalCosts = 0;

    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    allFeeders
      .map((feeder) => {
        const feederFeed =
          feeder.genderFeedRatios
            .map((ratio) => ratio.feed)
            .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

        return {
          kg:
            feeder.type === FeederType.START_FEEDER
              ? feederFeed +
                this.calculationsService.getDivergingEnergyFromGrowthStartStartWeight(
                  calculation,
                  simulation,
                  //calculation.extendedComparisonSimulation,
                )
              : feeder.type === FeederType.GROWTH_FEEDER
                ? feederFeed
                : feederFeed +
                  this.calculationsService.getDivergingEnergyFromGrowthEndDeliveredWeight(
                    calculation,
                    simulation,
                    livingWeight,
                    calculationLivingWeight,
                  ),
          costs: feeder.product.grossPricePer100Kg,
        };
      })
      .forEach((feeder) => {
        totalCosts += feeder.costs * feeder.kg * 0.01;
      });

    return totalCosts / (livingWeight - simulation.startWeight);
  }

  getEnergyConversionGenderSpecific(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    genderType: GenderTypes,
  ) {
    const averageEnergyConversion = simulation?.extendedBaseLine?.averageEnergyConversion;
    return calculation?.genders === GenderOptions.BOAR_GILT &&
      calculation?.country === CountryOptions.NL
      ? genderType === GenderTypes.BOAR
        ? averageEnergyConversion - 0.1
        : averageEnergyConversion + 0.1
      : genderType === GenderTypes.BARROW
        ? averageEnergyConversion + 0.075
        : averageEnergyConversion - 0.075;
  }

  getFeedGrowFinisherNeeds(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    genderType: GenderTypes,
    calculationLivingWeight?: number,
  ) {
    calculationLivingWeight =
      calculationLivingWeight ?? this.calculationsService.getCalculationLivingWeight(calculation);

    const energyConversion = this.getEnergyConversionGenderSpecific(
      calculation,
      simulation,
      genderType,
    );

    const feedGrowFinisherNeeds =
      (calculationLivingWeight - calculation.extendedBaseLine.startWeight) * energyConversion;
    return feedGrowFinisherNeeds;
  }

  getDivergingDaysGrowth(calculation: Calculation, calculationLivingWeight?: number) {
    calculationLivingWeight =
      calculationLivingWeight || this.calculationsService.getCalculationLivingWeight(calculation);
    const growthPerSlaughteredKgCalculation =
      (calculationLivingWeight - calculation.extendedBaseLine?.startWeight) /
      (calculation.extendedBaseLine?.growth * 0.001);

    const growthPerSlaughteredKgSimulation =
      (calculationLivingWeight - calculation.extendedBaseLine?.startWeight) /
      (calculation.extendedSimulation?.extendedBaseLine?.growth * 0.001);
    const divergingDaysGrowth =
      (growthPerSlaughteredKgCalculation - growthPerSlaughteredKgSimulation) * -1;

    return divergingDaysGrowth;
  }

  getAllEnergyDivergencesFromFeedAndGrowth(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight =
      livingWeight || this.calculationsService.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight || this.calculationsService.getCalculationLivingWeight(calculation);

    const divergingEnergyStartWeight =
      this.calculationsService.getDivergingEnergyFromGrowthStartStartWeight(
        calculation,
        simulation,
        //comparisonSim,
      );
    const divergingEnergyEndFeed =
      this.calculationsService.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        calculationLivingWeight,
      );

    const divergingEnergyGrowth =
      this.getDivergingDaysGrowth(calculation, calculationLivingWeight) * 1.1;
    const finalEnergyDivergence =
      divergingEnergyStartWeight + divergingEnergyEndFeed + divergingEnergyGrowth;

    return finalEnergyDivergence;
  }

  getManureCubeProduction(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    isComparison: boolean,
    totalGrowth?: number,
    feedConversion?: number,
    PigFeedSum?: number,
    livingWeight?: number,
    amountOfDays?: number,
  ) {
    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    totalGrowth =
      totalGrowth ??
      this.getTotalGrowth(calculation, simulation, isComparison, livingWeight, amountOfDays);
    PigFeedSum = PigFeedSum ?? this.getPigFeedSum(calculation, simulation);
    feedConversion =
      feedConversion ??
      this.calculationsService.getFeedConversion(simulation, PigFeedSum, livingWeight);

    const feedInKG = (totalGrowth / 1000) * feedConversion * 365;
    const allFeeders = simulation.feeders
      ? simulation.calculatedFeeder
        ? [...(simulation.feeders ?? []), simulation.calculatedFeeder]
        : simulation.feeders
      : [];

    const feedProductContributions = allFeeders.map((feeder) => {
      const feederFeed =
        feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => (prev ?? 0) + (cur ?? 0), 0) / feeder.genderFeedRatios.length;

      return {
        product: feeder.product,
        contributionPercentage: (feederFeed ?? 0) / PigFeedSum,
      };
    });

    let reSum = 0;
    feedProductContributions.forEach(
      (feeder) => (reSum += feeder.product.proteinGramPerKg * feeder.contributionPercentage),
    );
    const reFeed = feedInKG * reSum;
    const meatProduct = (totalGrowth / 1000) * 365;
    const reMeat = meatProduct * 25 * 6.25;
    const reRemainder = reFeed - reMeat;

    const urineProduction =
      0.0045 * reRemainder +
      0.035 * FEED_PROFIT_MANURE_VALUES.K * feedInKG +
      FEED_PROFIT_MANURE_VALUES.Na * feedInKG * 0.055;
    const fecesProduction = feedInKG * 0.16 * 0.88 * 2.2;
    return (urineProduction + fecesProduction) / 1000;
  }

  calculateRemainingEndFeederRatios(
    calculation: Calculation,
    simulation?: ExtendedSimulation,
    livingWeight?: number,
    calculationLivingWeight?: number,
    discounts?: Discount,
  ) {
    // Map the input simulation, some methods are shared between quick and extended simulations,
    // thus the same essential properties are needed in the same structure.
    simulation = this.mapSimulationForGenericUse(
      simulation ?? calculation.extendedSimulation,
      discounts,
    );

    livingWeight =
      livingWeight ?? this.calculationsService.getLivingWeight(calculation, simulation);
    calculationLivingWeight =
      calculationLivingWeight ?? this.calculationsService.getCalculationLivingWeight(calculation);
    const calculatedFeederEnergy = calculation.calculatedFeeder?.product?.ewKg ?? 0;

    const allEnergyDivergencesFromFeedAndGrowth = this.getAllEnergyDivergencesFromFeedAndGrowth(
      calculation,
      simulation,
      livingWeight,
    );

    const amountOfNeededTotalFeedBoar =
      this.getFeedGrowFinisherNeeds(
        calculation,
        simulation,
        GenderTypes.BOAR,
        calculationLivingWeight,
      ) ?? 0;
    const amountOfNeededTotalFeedBarrow =
      this.getFeedGrowFinisherNeeds(
        calculation,
        simulation,
        GenderTypes.BARROW,
        calculationLivingWeight,
      ) ?? 0;
    const amountOfNeededTotalFeedGilt =
      this.getFeedGrowFinisherNeeds(
        calculation,
        simulation,
        GenderTypes.GILT,
        calculationLivingWeight,
      ) ?? 0;
    const gilttest = this.calculationsService.getFeedOfGrowFinisherGender(
      calculation,
      GenderTypes.GILT,
      simulation.feeders,
    );
    const amountOfEnergyBoar = amountOfNeededTotalFeedBoar + allEnergyDivergencesFromFeedAndGrowth;
    const amountOfEndFeedBoar =
      (amountOfEnergyBoar -
        this.calculationsService.getFeedOfGrowFinisherGender(
          calculation,
          GenderTypes.BOAR,
          simulation.feeders,
        )) /
      calculatedFeederEnergy;
    const amountOfEnergyGilt = amountOfNeededTotalFeedGilt + allEnergyDivergencesFromFeedAndGrowth;
    const amountOfEndFeedGilt = (amountOfEnergyGilt - gilttest) / calculatedFeederEnergy;

    const amountOfEnergyBarrow =
      amountOfNeededTotalFeedBarrow + allEnergyDivergencesFromFeedAndGrowth;

    const amountOfFeedBarrow = this.calculationsService.getFeedOfGrowFinisherGender(
      calculation,
      GenderTypes.BARROW,
      simulation.feeders,
    );
    const amountOfEndFeedBarrow =
      (amountOfEnergyBarrow - amountOfFeedBarrow) / calculatedFeederEnergy;

    return {
      boar: amountOfEndFeedBoar,
      gilt: amountOfEndFeedGilt,
      barrow: amountOfEndFeedBarrow,
    };
  }

  calculateRemainingEndFeederRatiosComparisonSim(calculation: Calculation, livingWeight?: number) {
    livingWeight = livingWeight ?? this.calculationsService.getCalculationLivingWeight(calculation);
    const calculatedFeederEnergy = calculation.calculatedFeeder?.product?.ewKg ?? 0;
    const amountOfNeededTotalFeedBoar =
      this.getFeedGrowFinisherNeeds(
        calculation,
        calculation.extendedComparisonSimulation,
        GenderTypes.BOAR,
        livingWeight,
      ) ?? 0;
    const amountOfNeededTotalFeedBarrow =
      this.getFeedGrowFinisherNeeds(
        calculation,
        calculation.extendedComparisonSimulation,
        GenderTypes.BARROW,
        livingWeight,
      ) ?? 0;
    const amountOfNeededTotalFeedGilt =
      this.getFeedGrowFinisherNeeds(
        calculation,
        calculation.extendedComparisonSimulation,
        GenderTypes.GILT,
        livingWeight,
      ) ?? 0;
    const gilttest = this.calculationsService.getFeedOfGrowFinisherGender(
      calculation,
      GenderTypes.GILT,
    );
    const amountOfEndFeedBoar =
      (amountOfNeededTotalFeedBoar -
        this.calculationsService.getFeedOfGrowFinisherGender(calculation, GenderTypes.BOAR)) /
      calculatedFeederEnergy;
    const amountOfEndFeedGilt = (amountOfNeededTotalFeedGilt - gilttest) / calculatedFeederEnergy;
    const amountOfEndFeedBarrow =
      (amountOfNeededTotalFeedBarrow -
        this.calculationsService.getFeedOfGrowFinisherGender(calculation, GenderTypes.BARROW)) /
      calculatedFeederEnergy;

    return {
      boar: amountOfEndFeedBoar,
      gilt: amountOfEndFeedGilt,
      barrow: amountOfEndFeedBarrow,
    };
  }

  mapSimulationForGenericUse(
    simulation: ExtendedSimulation,
    discounts?: Discount,
  ): ExtendedSimulation {
    const returnSimulation: ExtendedSimulation = {
      ...simulation,
      feeders: this.calculationsService.getCalculatedFeedersWithDiscounts(
        simulation.feeders,
        discounts,
      ),

      startWeight: simulation?.extendedBaseLine?.startWeight,
      pigletPrice: simulation?.extendedBaseLine?.growFinisherPrice,
      slaughteredWeightInKg: simulation?.extendedBaseLine?.slaughteredWeightInKg,
      feedPackagePrice: simulation?.extendedBaseLine?.feedPackagePrice,
      revenuePerKg: simulation?.extendedBaseLine?.revenuePerKg,
    };
    return returnSimulation;
  }

  getComparisonSimulation(calculation: Calculation) {
    let incompleteSimulation = <ExtendedSimulation>{
      startWeight: calculation?.extendedBaseLine?.startWeight,
      pigletPrice: calculation?.extendedBaseLine?.growFinisherPrice,
      slaughteredWeightInKg: calculation?.extendedBaseLine?.slaughteredWeightInKg,
      revenuePerKg: calculation?.extendedBaseLine?.revenuePerKg,
      feedPackagePrice: null,

      feeders: calculation?.feeders?.length > 0 ? calculation?.feeders : null,
      calculatedFeeder: calculation?.calculatedFeeder,
      baseLine: calculation?.baseLine,
      extendedBaseLine: <ExtendedSimulationExtendedBaseLine>{
        ...calculation?.extendedBaseLine,
        feedPackagePrice: null,
      },
    };
    incompleteSimulation = {
      ...incompleteSimulation,
      feedPackagePrice: this.getFeedPackagePrice(incompleteSimulation),
      extendedBaseLine: <ExtendedSimulationExtendedBaseLine>{
        ...incompleteSimulation.extendedBaseLine,
        feedPackagePrice: this.getFeedPackagePrice(incompleteSimulation),
      },
    };

    // This method is called by the feeder ratio calculation as well
    // we should check before calculating that this does not result in recursion
    const calculatedFeederRatios = this.calculateRemainingEndFeederRatiosComparisonSim(calculation);
    return <ExtendedSimulation>{
      ...incompleteSimulation,
      calculatedFeeder: {
        ...incompleteSimulation.calculatedFeeder,
        genderFeedRatios: [
          {
            name: GenderTypes.GILT,
            feed: calculatedFeederRatios.gilt ?? 0,
          },
          calculation.genders === GenderOptions.BOAR_GILT
            ? {
                name: GenderTypes.BOAR,
                feed: calculatedFeederRatios.boar ?? 0,
              }
            : {
                name: GenderTypes.BARROW,
                feed: calculatedFeederRatios.barrow ?? 0,
              },
        ],
      },
    };
  }

  getSimulation(calculation: Calculation, simulation: ExtendedSimulation) {
    let incompleteSimulation = <ExtendedSimulation>{
      startWeight: simulation?.extendedBaseLine?.startWeight,
      pigletPrice: simulation?.extendedBaseLine?.growFinisherPrice,
      slaughteredWeightInKg: simulation?.extendedBaseLine?.slaughteredWeightInKg,
      revenuePerKg: simulation?.extendedBaseLine?.revenuePerKg,
      feedPackagePrice: null,

      feeders: simulation?.feeders?.length > 0 ? simulation?.feeders : [],
      calculatedFeeder: simulation?.calculatedFeeder,
      baseLine: simulation?.baseLine,
      extendedBaseLine: <ExtendedSimulationExtendedBaseLine>{
        ...simulation?.extendedBaseLine,
        feedPackagePrice: null,
      },
    };
    incompleteSimulation = {
      ...incompleteSimulation,
      feedPackagePrice: this.getFeedPackagePrice(incompleteSimulation),
      extendedBaseLine: <ExtendedSimulationExtendedBaseLine>{
        ...incompleteSimulation.extendedBaseLine,
        feedPackagePrice: this.getFeedPackagePrice(incompleteSimulation),
      },
    };

    return <ExtendedSimulation>{
      ...incompleteSimulation,
      extendedBaseLine: {
        ...incompleteSimulation.extendedBaseLine,
        fertilizerPerGrowFinisherInCubicMeters: this.getManureCubeProduction(
          calculation,
          incompleteSimulation,
          false,
        ),
      },
    };
  }

  calculateExtendedSimulation(
    calculation: Calculation,
    simulation: ExtendedSimulation,
    index: number,
    discounts?: Discount,
  ): ExtendedSimulationFullObject {
    const isComparison = index === 0;
    // Map the input simulation, some methods are shared between quick and extended simulations,
    // thus the same essential properties are needed in the same structure.
    simulation = isComparison
      ? this.getComparisonSimulation(calculation)
      : this.getSimulation(calculation, simulation);
    simulation = this.mapSimulationForGenericUse(simulation, discounts);
    const comparisonSimulation = this.mapSimulationForGenericUse(
      this.getComparisonSimulation(calculation),
      discounts,
    );

    calculation = {
      ...calculation,
      extendedComparisonSimulation: comparisonSimulation,
    };
    //occupancyRate calculation
    const feedPackagePrice = this.calculationsService.roundToExcelStandards(
      this.getFeedPackagePrice(simulation),
    );

    const calculationLivingWeight = this.calculationsService.roundToExcelStandards(
      this.calculationsService.getCalculationLivingWeight(calculation),
    );
    const livingWeight = this.calculationsService.roundToExcelStandards(
      this.calculationsService.getLivingWeight(calculation, simulation),
    );
    const amountOfDays = this.calculationsService.roundToExcelStandards(
      this.getAmountOfDays(calculation, simulation, false, livingWeight, calculationLivingWeight),
    );
    const totalgrowth = this.calculationsService.roundToExcelStandards(
      this.getTotalGrowth(calculation, simulation, false, livingWeight, amountOfDays),
    );
    const occupancyRate = this.calculationsService.roundToExcelStandards(
      this.getOccupancyRate(calculation, simulation, false, livingWeight, totalgrowth),
    );

    //feed conversion calculation
    const PigFeedSum = this.calculationsService.roundToExcelStandards(
      this.getPigFeedSum(calculation, simulation, false, livingWeight),
    );
    const feedConversion = this.calculationsService.getFeedConversion(
      simulation,
      PigFeedSum,
      livingWeight,
    );

    //feed price calculation
    const feedPrice = this.getFeedPrice(simulation, PigFeedSum, livingWeight, feedPackagePrice);

    //final difference delivered setup calculation
    const averageEnergy = this.getAverageEnergy(calculation, simulation);
    const avergeEnergyConversion = this.getAverageEnergyConversion(
      calculation,
      simulation,
      PigFeedSum,
      averageEnergy,
      livingWeight,
    );

    const feedCostTotalCostTotalKgForPacketPrice =
      this.getFeedCostTotalCostTotalKgForPacketPriceForFeedCosts(simulation);

    const packetTotalKg = feedCostTotalCostTotalKgForPacketPrice.totalKg;
    const packetPricePerKg = feedCostTotalCostTotalKgForPacketPrice.feedcosts;

    const feedCosts = this.getFeedCosts(
      calculation,
      simulation,
      undefined,
      averageEnergy,
      undefined,
    );

    //costs death and spoilage calculation
    const pigletPrice = this.calculationsService.getPigletPrice(calculation, simulation);
    const avgAmountDeliveredGrowFinisher = this.getAverageDeliveredGrowFinisher(
      calculation,
      simulation,
      false,
      occupancyRate,
    );
    const profitGrowFinisher = this.calculationsService.getProfitGrowFinisher(simulation);

    const growFinisherWeight = livingWeight - simulation.extendedBaseLine.startWeight;

    const profitGrowFinisherPerKg = profitGrowFinisher / growFinisherWeight;

    const pigletPricePerKg = pigletPrice / growFinisherWeight;

    const costsDeathsAndSpoilage = this.getCostDeathsAndSpoilage(
      calculation,
      simulation,
      false,
      avgAmountDeliveredGrowFinisher,
      avergeEnergyConversion,
      averageEnergy,
      feedCosts,
      profitGrowFinisher,
      feedPackagePrice,
    );

    const costDeathsAndSpoilagePerKg = costsDeathsAndSpoilage / growFinisherWeight;

    //only needs to be calculated if it isnt the first comparison line
    let finalDifferenceAveragePresentGrowFinisher = 0;
    let finalDifferenceYear = 0;
    const feedProfitYear = 0;

    //feed profit calculations
    const feedProfitAverageGrowFinisher = this.getFeedProfitAverageDeliveredGrowFinisher(
      calculation,
      simulation,
      profitGrowFinisher,
      pigletPrice,
      feedCosts,
      costsDeathsAndSpoilage,
    );
    const feedProfitDelivered = feedProfitAverageGrowFinisher;
    const feedProfitDeliveredPerKg = feedProfitAverageGrowFinisher / growFinisherWeight;

    const amountOfRounds = this.getRounds(
      calculation,
      simulation,
      false,
      amountOfDays,
      occupancyRate,
    );
    const feedProfitAverageGrowFinisherInYear = amountOfRounds * profitGrowFinisher;
    const pigletPriceInYear = amountOfRounds * pigletPrice;
    const feedCostsInYear = amountOfRounds * feedCosts;
    const costsDeathsAndSpoilageInYear = amountOfRounds * costsDeathsAndSpoilage;
    const feedprofitAverageGrowFinisherYear =
      feedProfitAverageGrowFinisherInYear -
      pigletPriceInYear -
      feedCostsInYear -
      costsDeathsAndSpoilageInYear;

    //manure related calculations
    const manureCostPerPig = this.getManureCostPerPig(
      calculation,
      simulation,
      false,
      totalgrowth,
      feedConversion,
      PigFeedSum,
      livingWeight,
      amountOfDays,
    );

    const manureCubedProduction =
      manureCostPerPig / calculation.extendedBaseLine.fertilizerPricePerCubicMeter;

    const feedProfitPerSquareMeterIncludingFertilizer =
      (feedprofitAverageGrowFinisherYear - manureCostPerPig) /
      calculation.extendedBaseLine.squareMeterPerGrowFinisher;

    const comparisonSimFeedProfitPerSquareMeterIncludingFertilizer =
      this.getFeedProfitPerSquareMeterIncludingFertilizer(calculation, comparisonSimulation, true);
    const comparisonSimManureCostPerPig = this.getManureCostPerPig(
      calculation,
      comparisonSimulation,
      true,
    );
    const comparisonSimDeliveredGrowFinisher = this.getAverageDeliveredGrowFinisher(
      calculation,
      comparisonSimulation,
      true,
    );
    const comparisonSimFeedProfitAveragePresentGrowFinisher =
      this.getFeedProfitAveragePresentGrowFinisher(calculation, comparisonSimulation, true);
    const feedProfitAveragePresentGrowFinisher = this.getFeedProfitAveragePresentGrowFinisher(
      calculation,
      simulation,
      false,
      amountOfRounds,
      profitGrowFinisher,
      pigletPrice,
      feedCosts,
      costsDeathsAndSpoilage,
    );

    const finalDifferenceSimulation = this.getFinalDifferenceDelivered(
      calculation,
      simulation,
      false,
      profitGrowFinisher,
      feedCosts,
      costsDeathsAndSpoilage,
      pigletPrice,
    );
    const finalDifferenceComparison = this.getFinalDifferenceDelivered(
      calculation,
      comparisonSimulation,
      false,
    );
    const finalDifferenceDelivered =
      (finalDifferenceSimulation ?? 0) - (finalDifferenceComparison ?? 0);

    finalDifferenceYear =
      ((comparisonSimFeedProfitAveragePresentGrowFinisher - comparisonSimManureCostPerPig) *
        comparisonSimDeliveredGrowFinisher -
        (feedProfitAveragePresentGrowFinisher - manureCostPerPig) *
          avgAmountDeliveredGrowFinisher) *
      -1;

    finalDifferenceAveragePresentGrowFinisher =
      feedProfitPerSquareMeterIncludingFertilizer -
      comparisonSimFeedProfitPerSquareMeterIncludingFertilizer;
    const averageAmountOfPresentDeliveredGrowFinishers =
      this.getAverageAmountOfPresentDeliveredGrowFinishers(calculation, simulation, occupancyRate);
    const divergingDaysStartWeight = this.getDivergingDaysStartWeight(calculation, simulation);
    const divergingDaysSlaughteredWeight = this.getDivergingDaysSlaughteredWeight(
      calculation,
      simulation,
      livingWeight,
      calculationLivingWeight,
    );
    const divergingDaysGrowth = this.getDivergingDaysGrowth(calculation, calculationLivingWeight);
    const meatPercentage = this.getMeatPercentage(calculation, simulation);
    const milimeterMuscle = this.getMilimeterMuscle(calculation, simulation);
    const milimeterLard = this.getMilimeterLard(calculation, simulation);
    const divergingEnergyFromGrowthStartStartWeight =
      this.calculationsService.getDivergingEnergyFromGrowthStartStartWeight(
        calculation,
        simulation,
        //calculation.extendedComparisonSimulation,
      );
    const divergingEnergyFromGrowthEndDeliveredWeight =
      this.calculationsService.getDivergingEnergyFromGrowthEndDeliveredWeight(
        calculation,
        simulation,
        livingWeight,
        calculationLivingWeight,
      );
    const divergingEnergyDivergingGrowth =
      this.calculationsService.getDivergingEnergyFromGrowthStartStartWeight(
        calculation,
        simulation,
        //calculation.extendedComparisonSimulation,
      );
    const genderTypeBarrowOrBoar =
      calculation.genders === GenderOptions.BARROW_GILT ? GenderTypes.BARROW : GenderTypes.BOAR;
    const energyConversionBoarOrBarrow = this.getEnergyConversionGenderSpecific(
      calculation,
      simulation,
      genderTypeBarrowOrBoar,
    );
    const energyConversionGilt = this.getEnergyConversionGenderSpecific(
      calculation,
      simulation,
      GenderTypes.GILT,
    );
    const effectOnFeedCostsStartWeightHas = this.getEffectOnFeedCostsStartWeightHas(
      calculation,
      simulation,
    );
    const effectOnFeedCostsSlaughteredWeightHas = this.getEffectOnFeedCostsSlaughteredWeightHas(
      calculation,
      simulation,
    );
    const effectOnFeedCostsGrowthHas = this.getEffectOnFeedCostsGrowthHas(
      calculation,
      simulation,
      feedPackagePrice,
    );
    const energyUsed = this.getEnergyUsed(calculation, simulation, undefined, livingWeight);
    const feedCostPerKgPerGrowth = this.getFeedCostPerKgPerGrowth(
      calculation,
      simulation,
      livingWeight,
      calculationLivingWeight,
    );
    const mortalityPercentage = simulation.extendedBaseLine.mortalityPercentage;
    const feedProfitAverageGrowFinisherInclFertilizer =
      feedprofitAverageGrowFinisherYear - manureCostPerPig;

    //[35.41, mm]
    return {
      isComparisonSim: index === 0,

      startWeight: simulation.extendedBaseLine.startWeight,
      pigletPrice: simulation.extendedBaseLine.growFinisherPrice,
      pigletPricePerKg,
      slaughteredWeightInKg: simulation.extendedBaseLine.slaughteredWeightInKg,
      livingWeight,
      revenuePerKg: simulation.extendedBaseLine.revenuePerKg,
      feedPackagePrice,
      occupancyRate,
      amount: amountOfRounds,
      growth: totalgrowth,
      feedConversion,
      feedPrice,
      finalDifferenceDelivered,
      finalDifferenceAveragePresentGrowFinisher,
      finalDifferenceYear,
      feedProfitDelivered,
      feedProfitDeliveredPerKg,
      feedProfitYear,
      feedProfitAverage: feedProfitPerSquareMeterIncludingFertilizer,

      avgAmountDeliveredGrowFinisher,
      averageAmountOfPresentDeliveredGrowFinishers,
      divergingDaysStartWeight,
      divergingDaysSlaughteredWeight,
      divergingDaysGrowth,

      amountOfDays,
      meatPercentage,
      mortalityPercentage,
      milimeterMuscle,
      milimeterNeck: milimeterLard,
      amountOfRounds,
      divergingEnergyFromGrowthStartStartWeight,
      divergingEnergyFromGrowthEndDeliveredWeight,
      divergingEnergyDivergingGrowth,
      energyConversion: simulation.extendedBaseLine.averageEnergyConversion,
      energyConversionBoarOrBarrow,
      energyConversionGilt,
      avergeEnergyConversion,
      effectDifferentStartWeightOnFeedCosts: effectOnFeedCostsStartWeightHas, //J26
      effectSlaughteredWeightOnFeedCost: effectOnFeedCostsSlaughteredWeightHas,
      effectDifferenceGrowthOnFeedCost: effectOnFeedCostsGrowthHas,
      energyUse: energyUsed,
      packetTotalKg,
      packetPricePerKg,
      feedCostPerKgPerGrowth,
      feedProfitPerDeliveredGrowFinisher: feedProfitAverageGrowFinisher,
      profitPerGrowFinisher: profitGrowFinisher,
      profitGrowFinisherPerKg,
      feedCosts,
      costDeathsAndSpoilage: costsDeathsAndSpoilage,
      costDeathsAndSpoilagePerKg,

      feedProfitAverageGrowFinisherInYear,
      pigletPriceInYear,
      feedCostsInYear,
      costsDeathsAndSpoilageInYear,
      feedprofitAverageGrowFinisherYear,

      squareMeterPerGrowFinisher: calculation.extendedBaseLine.squareMeterPerGrowFinisher,
      fertilizerPricePerCubicMeter: calculation.extendedBaseLine.fertilizerPricePerCubicMeter,
      manureCubedProduction,
      fertilizerCostsPerGrowFinisher: manureCostPerPig,
      feedProfitAverageGrowFinisherInclFertilizer,
      feedProfitPerSquareMeterInclFertilizer: feedProfitPerSquareMeterIncludingFertilizer,
    };
  }
}
